// Repeat controls: type, offset ratio, pan (wrap-around) and the pattern
// preview options (tile count, seam check, zoom, reset pan).
//
// Pan X/Y are the SAME state the canvas drag writes to, so the inputs and the
// drag interaction always stay in sync. Values are wrapped into [0, tileW/H)
// on commit, mirroring the (x+dx) mod W math used by the preview.

import React, { useState } from 'react';
import type { RepeatOptions, RepeatType } from '@imagelayerdecomposer/core';

export interface RepeatView {
  /** Number of tiles per axis shown in the preview, or 'fit' for auto. */
  count: number | 'fit';
  /** Draw the tile seam guides on the canvas. */
  seamCheck: boolean;
}

interface RepeatPanelProps {
  repeat: RepeatOptions;
  onRepeatChange: (patch: Partial<RepeatOptions>) => void;
  view: RepeatView;
  onViewChange: (patch: Partial<RepeatView>) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onResetPan: () => void;
}

const RATIO_OPTIONS: { value: number; label: string }[] = [
  { value: 0.5, label: '1/2' },
  { value: 1 / 3, label: '1/3' },
  { value: 0.25, label: '1/4' },
];

const COUNT_OPTIONS: { value: number | 'fit'; label: string }[] = [
  { value: 2, label: '2×2' },
  { value: 3, label: '3×3' },
  { value: 4, label: '4×4' },
  { value: 'fit', label: 'Fit' },
];

function matchRatio(ratio: number): string {
  for (const opt of RATIO_OPTIONS) {
    if (Math.abs(opt.value - ratio) < 1e-3) return opt.value.toString();
  }
  return 'free';
}

function wrap(value: number, mod: number): number {
  if (!Number.isFinite(mod) || mod <= 0) return 0;
  return ((value % mod) + mod) % mod;
}

export default function RepeatPanel({
  repeat,
  onRepeatChange,
  view,
  onViewChange,
  zoom,
  onZoomChange,
  onResetPan,
}: RepeatPanelProps) {
  // The ratio select uses KEY strings ('0.5', '0.333…', '0.25', 'free'), not
  // numeric values: a raw numeric `value` never matches an <option> for a
  // custom ratio and browsers then show the first option (1/2), which made the
  // control lie about the real offset. "Free" is kept as local UI state so
  // choosing it is not immediately overridden by matchRatio() re-detecting the
  // current preset value.
  const [ratioMode, setRatioMode] = useState<string>(() => matchRatio(repeat.ratio));
  const tileW = repeat.tileW || 1;
  const tileH = repeat.tileH || 1;

  const commitPan = (dx: number, dy: number) => {
    onRepeatChange({ dx: wrap(dx, tileW), dy: wrap(dy, tileH) });
  };

  return (
    <div className="panel">
      <div className="panel-header">
        <span>Repeat</span>
      </div>

      <div className="control-row">
        <label>Type:</label>
        <select
          value={repeat.type}
          onChange={(e) => onRepeatChange({ type: e.target.value as RepeatType })}
        >
          <option value="grid">Grid</option>
          <option value="half-drop">Half-drop</option>
          <option value="brick">Brick</option>
        </select>
      </div>

      <div className="control-row inline">
        <label>Ratio:</label>
        <select
          value={ratioMode}
          onChange={(e) => {
            const v = e.target.value;
            setRatioMode(v);
            if (v === 'free') {
              // Keep the current ratio as the starting point for free mode.
              onRepeatChange({ ratio: Number(repeat.ratio.toFixed(3)) || 0.5 });
            } else {
              onRepeatChange({ ratio: parseFloat(v) });
            }
          }}
        >
          {RATIO_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value.toString()}>
              {opt.label}
            </option>
          ))}
          <option value="free">Free</option>
        </select>
      </div>

      {ratioMode === 'free' && (
        <div className="control-row inline">
          <label>Offset (0–1):</label>
          <input
            type="number"
            min={0}
            max={1}
            step={0.01}
            value={Number(repeat.ratio.toFixed(3))}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (Number.isFinite(v)) {
                onRepeatChange({ ratio: Math.max(0, Math.min(1, v)) });
              }
            }}
          />
        </div>
      )}

      <div className="control-row inline">
        <label>Pan X:</label>
        <input
          type="number"
          value={Math.round(repeat.dx)}
          onChange={(e) => commitPan(parseFloat(e.target.value) || 0, repeat.dy)}
        />
        <span className="unit">/ {tileW}px</span>
      </div>
      <div className="control-row inline">
        <label>Pan Y:</label>
        <input
          type="number"
          value={Math.round(repeat.dy)}
          onChange={(e) => commitPan(repeat.dx, parseFloat(e.target.value) || 0)}
        />
        <span className="unit">/ {tileH}px</span>
      </div>

      <div className="hint small">
        Drag on the canvas in “Repeat preview” to pan; the offset wraps around
        so seams stay continuous.
      </div>

      <div className="field-block">
        <label className="field-label">Preview tiles</label>
        <div className="segmented" role="group" aria-label="Preview tile count">
          {COUNT_OPTIONS.map((opt) => (
            <button
              key={String(opt.value)}
              type="button"
              className={`seg-btn${view.count === opt.value ? ' active' : ''}`}
              aria-pressed={view.count === opt.value}
              onClick={() => onViewChange({ count: opt.value })}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <label className="check-row">
        <input
          type="checkbox"
          checked={view.seamCheck}
          onChange={(e) => onViewChange({ seamCheck: e.target.checked })}
        />
        Seam check
      </label>

      <div className="field-block">
        <label className="field-label" htmlFor="preview-zoom">
          Preview zoom: <span className="value-badge">{zoom}%</span>
        </label>
        <input
          id="preview-zoom"
          type="range"
          min={25}
          max={300}
          step={5}
          value={zoom}
          onChange={(e) => onZoomChange(parseInt(e.target.value, 10))}
        />
      </div>

      <div className="btn-row">
        <button type="button" className="btn small" onClick={onResetPan}>
          Reset pan
        </button>
        <button type="button" className="btn small secondary" onClick={() => onZoomChange(100)}>
          Reset zoom
        </button>
      </div>
    </div>
  );
}
